import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowLeft, Phone, Key } from "lucide-react";

export default function DriverLogin() {
  const navigate = useNavigate();
  const { requestOtp, verifyOtp } = useAuth();
  const [phone, setPhone] = useState("+16045551234");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState("");

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await requestOtp(phone.trim());
      setStep(2);
      setDevCode(res.dev_code || "");
      toast.success("Code sent. Check console / dev code shown below.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyOtp(phone.trim(), code.trim());
      toast.success("Authenticated. Get to work.");
      navigate("/driver");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 md:p-12 grain relative bg-background">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-2" data-testid="back-home-link">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green mb-2">// DRIVER ACCESS</div>
          <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter">Phone Auth</h2>
          <p className="text-sm text-muted-foreground mt-2 font-mono uppercase tracking-widest">Step {step} of 2</p>
        </div>

        {step === 1 && (
          <form onSubmit={sendCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-widest">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" className="h-16 pl-10 bg-card border-2 text-lg font-mono" data-testid="driver-phone-input"/>
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-16 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold text-base" data-testid="driver-request-otp-btn">
              {busy ? "Sending..." : "Send OTP"}
            </Button>
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Demo driver: <span className="text-brand-green">+16045551234</span>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verify} className="space-y-4">
            {devCode && (
              <div className="border-2 border-dashed border-brand-green bg-brand-green/5 p-3 font-mono text-xs" data-testid="dev-code-banner">
                <div className="uppercase tracking-widest text-muted-foreground mb-1">DEV MODE // MOCK SMS</div>
                <div className="text-2xl font-bold text-brand-green tracking-widest">{devCode}</div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="code" className="font-mono text-xs uppercase tracking-widest">6-digit code</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="code" required maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" className="h-16 pl-10 bg-card border-2 text-2xl font-mono tracking-[0.5em] text-center" data-testid="driver-otp-input"/>
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-16 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold text-base" data-testid="driver-verify-otp-btn">
              {busy ? "Verifying..." : "Verify & Sign In"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setStep(1)} className="w-full font-mono uppercase text-xs" data-testid="driver-back-btn">
              Change number
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
